// =============================================================================
// Solar / PV sizing reference data + physics helpers
// -----------------------------------------------------------------------------
// All values are conservative engineering averages drawn from the NASA POWER
// (Surface Meteorology & Solar Energy) dataset and SAURAN ground stations,
// rounded to 1 decimal place. Peak Sun Hours (PSH) = kWh/m²/day at 1 kW/m²
// reference irradiance, i.e. equivalent hours of full-sun production.
//
// We expose BOTH:
//   · pshAnnual — long-term annual mean (use for typical-day sizing)
//   · pshWinter — June worst-case mean (use for guaranteed-runtime sizing,
//                 which is the standard approach for off-grid water pumping)
// =============================================================================

export const PV_REGIONS = [
  // ---- South Africa · Western Cape ----
  { id: "cape-town",     name: "Cape Town",           region: "Western Cape · ZA",        pshAnnual: 5.4, pshWinter: 3.4 },
  { id: "stellenbosch",  name: "Stellenbosch",        region: "Western Cape · ZA",        pshAnnual: 5.5, pshWinter: 3.5 },
  { id: "paarl",         name: "Paarl",               region: "Western Cape · ZA",        pshAnnual: 5.5, pshWinter: 3.5 },
  { id: "worcester",     name: "Worcester",           region: "Western Cape · ZA",        pshAnnual: 5.7, pshWinter: 3.8 },
  { id: "george",        name: "George",              region: "Garden Route · ZA",        pshAnnual: 5.2, pshWinter: 3.6 },
  { id: "knysna",        name: "Knysna",              region: "Garden Route · ZA",        pshAnnual: 5.2, pshWinter: 3.5 },
  { id: "mossel-bay",    name: "Mossel Bay",          region: "Garden Route · ZA",        pshAnnual: 5.4, pshWinter: 3.5 },
  { id: "plettenberg",   name: "Plettenberg Bay",     region: "Garden Route · ZA",        pshAnnual: 5.3, pshWinter: 3.6 },
  { id: "hermanus",      name: "Hermanus",            region: "Western Cape · ZA",        pshAnnual: 5.3, pshWinter: 3.4 },
  { id: "saldanha",      name: "Saldanha",            region: "West Coast · ZA",          pshAnnual: 5.5, pshWinter: 3.6 },
  { id: "vredendal",     name: "Vredendal",           region: "West Coast · ZA",          pshAnnual: 5.8, pshWinter: 3.9 },
  // ---- Northern Cape ----
  { id: "kimberley",     name: "Kimberley",           region: "Northern Cape · ZA",       pshAnnual: 6.2, pshWinter: 4.6 },
  { id: "upington",      name: "Upington",            region: "Northern Cape · ZA",       pshAnnual: 6.5, pshWinter: 5.0 },
  { id: "de-aar",        name: "De Aar",              region: "Northern Cape · ZA",       pshAnnual: 6.4, pshWinter: 4.8 },
  { id: "springbok",     name: "Springbok",           region: "Namaqualand · ZA",         pshAnnual: 6.2, pshWinter: 4.5 },
  { id: "kuruman",       name: "Kuruman",             region: "Northern Cape · ZA",       pshAnnual: 6.2, pshWinter: 4.7 },
  { id: "postmasburg",   name: "Postmasburg",         region: "Northern Cape · ZA",       pshAnnual: 6.3, pshWinter: 4.7 },
  // ---- Eastern Cape ----
  { id: "port-elizabeth", name: "Gqeberha (Port Elizabeth)", region: "Eastern Cape · ZA", pshAnnual: 5.5, pshWinter: 3.8 },
  { id: "east-london",   name: "East London",         region: "Eastern Cape · ZA",        pshAnnual: 5.3, pshWinter: 4.0 },
  { id: "mthatha",       name: "Mthatha",             region: "Eastern Cape · ZA",        pshAnnual: 5.3, pshWinter: 4.1 },
  { id: "graaff-reinet", name: "Graaff-Reinet",       region: "Eastern Cape · ZA",        pshAnnual: 6.0, pshWinter: 4.4 },
  { id: "cradock",       name: "Cradock",             region: "Eastern Cape · ZA",        pshAnnual: 5.9, pshWinter: 4.4 },
  { id: "queenstown",    name: "Komani (Queenstown)", region: "Eastern Cape · ZA",        pshAnnual: 5.7, pshWinter: 4.3 },
  { id: "jeffreys-bay",  name: "Jeffreys Bay",        region: "Eastern Cape · ZA",        pshAnnual: 5.5, pshWinter: 3.8 },
  // ---- KwaZulu-Natal ----
  { id: "durban",        name: "Durban",              region: "KwaZulu-Natal · ZA",       pshAnnual: 5.0, pshWinter: 3.8 },
  { id: "pmb",           name: "Pietermaritzburg",    region: "KwaZulu-Natal · ZA",       pshAnnual: 5.5, pshWinter: 4.0 },
  { id: "richards-bay",  name: "Richards Bay",        region: "KwaZulu-Natal · ZA",       pshAnnual: 5.0, pshWinter: 3.9 },
  { id: "newcastle",     name: "Newcastle",           region: "KwaZulu-Natal · ZA",       pshAnnual: 5.5, pshWinter: 4.1 },
  { id: "vryheid",       name: "Vryheid",             region: "KwaZulu-Natal · ZA",       pshAnnual: 5.4, pshWinter: 4.1 },
  // ---- Free State ----
  { id: "bloemfontein",  name: "Bloemfontein",        region: "Free State · ZA",          pshAnnual: 6.0, pshWinter: 4.5 },
  { id: "welkom",        name: "Welkom",              region: "Free State · ZA",          pshAnnual: 6.0, pshWinter: 4.5 },
  { id: "aliwal-north",  name: "Aliwal North",        region: "Free State · ZA",          pshAnnual: 6.0, pshWinter: 4.4 },
  // ---- Gauteng ----
  { id: "johannesburg",  name: "Johannesburg",        region: "Gauteng · ZA",             pshAnnual: 5.8, pshWinter: 4.5 },
  { id: "pretoria",      name: "Pretoria",            region: "Gauteng · ZA",             pshAnnual: 5.8, pshWinter: 4.5 },
  { id: "vereeniging",   name: "Vereeniging",         region: "Gauteng · ZA",             pshAnnual: 5.8, pshWinter: 4.4 },
  // ---- North West ----
  { id: "rustenburg",    name: "Rustenburg",          region: "North West · ZA",          pshAnnual: 5.9, pshWinter: 4.6 },
  { id: "mahikeng",      name: "Mahikeng",            region: "North West · ZA",          pshAnnual: 6.0, pshWinter: 4.7 },
  { id: "klerksdorp",    name: "Klerksdorp",          region: "North West · ZA",          pshAnnual: 6.0, pshWinter: 4.6 },
  { id: "vryburg",       name: "Vryburg",             region: "North West · ZA",          pshAnnual: 6.0, pshWinter: 4.6 },
  // ---- Mpumalanga ----
  { id: "nelspruit",     name: "Mbombela (Nelspruit)", region: "Mpumalanga · ZA",         pshAnnual: 5.5, pshWinter: 4.2 },
  { id: "witbank",       name: "eMalahleni (Witbank)", region: "Mpumalanga · ZA",         pshAnnual: 5.7, pshWinter: 4.4 },
  { id: "secunda",       name: "Secunda",             region: "Mpumalanga · ZA",          pshAnnual: 5.7, pshWinter: 4.3 },
  { id: "ermelo",        name: "Ermelo",              region: "Mpumalanga · ZA",          pshAnnual: 5.5, pshWinter: 4.2 },
  { id: "hoedspruit",    name: "Hoedspruit",          region: "Mpumalanga · ZA",          pshAnnual: 5.7, pshWinter: 4.4 },
  // ---- Limpopo ----
  { id: "polokwane",     name: "Polokwane",           region: "Limpopo · ZA",             pshAnnual: 5.7, pshWinter: 4.4 },
  { id: "thohoyandou",   name: "Thohoyandou",         region: "Limpopo · ZA",             pshAnnual: 5.5, pshWinter: 4.3 },
  { id: "lephalale",     name: "Lephalale (Ellisras)", region: "Limpopo · ZA",            pshAnnual: 6.0, pshWinter: 4.7 },
  { id: "tzaneen",       name: "Tzaneen",             region: "Limpopo · ZA",             pshAnnual: 5.5, pshWinter: 4.2 },
  // ---- SADC neighbours (HidroVerse operates cross-border) ----
  { id: "maputo",        name: "Maputo",              region: "Mozambique",               pshAnnual: 5.3, pshWinter: 4.0 },
  { id: "mbabane",       name: "Mbabane",             region: "Eswatini",                 pshAnnual: 5.6, pshWinter: 4.2 },
  { id: "maseru",        name: "Maseru",              region: "Lesotho",                  pshAnnual: 5.8, pshWinter: 4.3 },
  { id: "gaborone",      name: "Gaborone",            region: "Botswana",                 pshAnnual: 6.0, pshWinter: 4.7 },
  { id: "windhoek",      name: "Windhoek",            region: "Namibia",                  pshAnnual: 6.5, pshWinter: 5.0 },
  { id: "harare",        name: "Harare",              region: "Zimbabwe",                 pshAnnual: 5.8, pshWinter: 4.6 },
  { id: "bulawayo",      name: "Bulawayo",            region: "Zimbabwe",                 pshAnnual: 6.0, pshWinter: 4.7 },
  { id: "livingstone",   name: "Livingstone",         region: "Zambia",                   pshAnnual: 5.9, pshWinter: 4.7 },
  { id: "lilongwe",      name: "Lilongwe",            region: "Malawi",                   pshAnnual: 5.7, pshWinter: 4.6 },
];

// Common IEC standard motor sizes (kW). Lets the user select a pump in one click
// rather than typing nameplate values. "Other" lets them enter a custom value.
export const PUMP_SIZES_KW = [
  0.37, 0.55, 0.75, 1.1, 1.5, 2.2, 3, 4, 5.5, 7.5, 11, 15, 18.5, 22, 30, 37, 45, 55, 75, 90,
];

// Common PV module wattages in the SA market (2026). Bifacial bonus is excluded
// to keep sizing conservative; user can pick the panel they will actually source.
export const PV_PANEL_WATTAGES = [
  { w: 330, label: "330 W · poly / older mono" },
  { w: 380, label: "380 W · mono PERC" },
  { w: 415, label: "415 W · mono PERC" },
  { w: 450, label: "450 W · half-cell mono" },
  { w: 500, label: "500 W · half-cell mono" },
  { w: 550, label: "550 W · 144-cell mono (most common)" },
  { w: 580, label: "580 W · TOPCon" },
  { w: 600, label: "600 W · bifacial TOPCon" },
  { w: 615, label: "615 W · bifacial TOPCon" },
  { w: 670, label: "670 W · HJT bifacial" },
];

// -----------------------------------------------------------------------------
// Daylight assumptions for southern-African latitudes (-22° to -34°).
// Daylight = sunrise→sunset clock-hour window; PSH is integrated within it.
// June solstice → ~10.0–10.8 h; equinox → 12.0 h. Use conservative midpoints.
// -----------------------------------------------------------------------------
export const DAYLIGHT_HOURS = {
  annual: 12.0,
  winter: 10.5,
};

// -----------------------------------------------------------------------------
// Solar pump simulation (PV-direct with VFD / MPPT controller).
//
//   1. Approximate the day's irradiance as a half-sin² bell (a better fit for
//      real plane-of-array irradiance than a plain sine — peak is higher,
//      morning/evening shoulders drop off faster, matching the cosine-of-
//      zenith-angle losses that flatten real-world sunrise/sunset hours):
//
//          G(t) = G_peak × sin²(π × t / L)        t ∈ [0, L]
//
//      calibrated so the integral equals the published Peak Sun Hours:
//          ∫sin²(πt/L) dt = L/2  ⇒  G_peak = 2 × PSH / L
//
//      For Nelspruit winter (PSH 4.2, L 10.5 h) this gives G_peak ≈ 0.80
//      kW/m² — matching the POA noon irradiance that industry sizing tools
//      use internally.
//
//   2. Array DC power at time t:
//          P_dc(t) = arrayKwp × G(t) × η_system          [kW]
//
//   3. Pump operating regime (typical solar VFD controllers):
//          P_dc < P_cutIn          →   Q = 0          (pump off)
//          P_cutIn ≤ P_dc ≤ P_rated →  Q = Q_rated × (P_dc / P_rated)^α
//          P_dc > P_rated          →   Q = Q_rated   (controller clips power)
//
//      The exponent α is fitted from the user-supplied (or default) second
//      anchor point on the pump curve. For a typical centrifugal pump
//      Q(30 %) ≈ 0.55 × Q_rated, giving α ≈ 0.5 (Q ∝ √P), which is the
//      industry rule-of-thumb under VFD speed control.
//
//   4. Daily volume = trapezoidal integration of Q across the day.
//      Daily energy DELIVERED to the pump = trapezoidal integration of
//      P_use (the clipped DC power when the pump is on).
//
// All flow values in litres/min internally; convert at the UI boundary.
// -----------------------------------------------------------------------------
export function fitPumpCurveExponent({ qRated, qAt30 }) {
  // Q(0.30 × P_rated) / Q_rated = 0.30^α  →  α = log(ratio) / log(0.30)
  if (!qRated || qRated <= 0 || !qAt30 || qAt30 <= 0 || qAt30 >= qRated) return 0.5;
  const ratio = qAt30 / qRated;
  return Math.log(ratio) / Math.log(0.30);
}

// -----------------------------------------------------------------------------
// Operating-point correction
// -----------------------------------------------------------------------------
// Catalogue / nameplate flow is given at one point on the Q-H curve (usually
// the pump's BEP). Once the user adds static head + pipe friction, the
// operating point shifts LEFT on the curve and the delivered flow drops.
//
//   Parabolic Q-H curve:   H(Q) = H0 − k · Q²
//     H0 ≈ 1.25 × H_rated  (shutoff head for a typical centrifugal)
//     Solving for Q at H_system:
//       Q_op = Q_rated × √((H0 − H_system) / (H0 − H_rated))
//
//   If H_system ≥ H0   → pump can't lift to the discharge → Q_op = 0
//   If H_system ≤ H_rated → Q_op is approximated as the rated flow (the
//                            controller will clip on motor power anyway).
//
// This is the same correction Lorentz / Grundfos sizing tools apply
// internally when the user provides system TDH alongside the pump catalogue
// point. Without it, the simulation systematically over-estimates yield.
// -----------------------------------------------------------------------------
export function correctFlowForSystemHead({ qRated, hRated, hSystem, shutoffMultiplier = 1.25 }) {
  if (!qRated || qRated <= 0) return { qCorrected: 0, derateFactor: 0, shutoffReached: false };
  // If either head is missing, no correction is possible — pass through.
  if (!hRated || hRated <= 0 || hSystem == null || hSystem < 0) {
    return { qCorrected: qRated, derateFactor: 1, shutoffReached: false };
  }
  const h0 = hRated * shutoffMultiplier;
  if (hSystem >= h0) {
    return { qCorrected: 0, derateFactor: 0, shutoffReached: true };
  }
  if (hSystem <= hRated) {
    // Operating above BEP would mean Q > Q_rated. The motor / controller
    // caps power before this matters; report flat rated flow.
    return { qCorrected: qRated, derateFactor: 1, shutoffReached: false };
  }
  const ratio = (h0 - hSystem) / (h0 - hRated);
  const f = Math.sqrt(Math.max(0, ratio));
  return { qCorrected: qRated * f, derateFactor: f, shutoffReached: false };
}

export function simulateSolarPumpDay({
  pumpKw,                 // pump rated power (kW)
  qRatedLpm,              // flow at rated power (L/min)
  qAt30Lpm,               // flow at 30 % rated power (L/min) — optional
  cutInPct = 25,          // cut-in % of pump rated power (controller dependent)
  arrayKwp,               // installed array (kWp)
  psh,                    // Peak Sun Hours for the day (h)
  daylightHours,          // sunrise→sunset clock hours
  etaSystem = 0.75,       // umbrella system efficiency
  steps = 96,             // simulation resolution (15-min steps over 24 h)
  // ---- Optional dynamic head (flow-dependent friction) ----
  // If all three are provided AND > 0, the simulator recomputes the operating
  // head at each timestep:
  //     H(Q) = H_static + H_frictionRated × (Q / Q_rated)^1.85
  // Then re-derates Q using the parabolic Q-H curve. This is the engineering-
  // honest model — at part-load the pump faces less friction and delivers
  // more than a constant-TDH model predicts (typically +5–15 % on long-run
  // installations). One iteration converges to within ~1 % for any realistic
  // pump-curve / friction combination.
  hRatedM = 0,            // pump's rated head (m) — published at Q_rated
  hStaticM = 0,           // static head + outlet pressure (m, constant)
  hFrictionRatedM = 0,    // friction loss at Q_rated (m) — scales with Q^1.85
}) {
  if (!pumpKw || !qRatedLpm || !arrayKwp || !psh || !daylightHours) {
    return { volumeLitres: 0, energyKwh: 0, pumpingHours: 0, peakFlowLpm: 0, runsAtAll: false };
  }
  const alpha = fitPumpCurveExponent({
    qRated: qRatedLpm,
    qAt30: qAt30Lpm || qRatedLpm * 0.55,
  });
  const P_cutIn = pumpKw * (cutInPct / 100);
  const G_peak  = (2 * psh) / daylightHours;               // kW/m² — sin² calibration
  const L = daylightHours;
  const dt = L / steps;                                    // hours per step
  let volume = 0;
  let energy = 0;
  let pumpingMinutes = 0;
  let peakFlow = 0;

  const dynamicHead = hRatedM > 0 && (hStaticM > 0 || hFrictionRatedM > 0);
  const h0 = hRatedM * 1.25;                               // shutoff head approx

  for (let i = 0; i <= steps; i++) {
    const t = i * dt;                                      // hours into daylight
    const sinNorm = Math.sin((Math.PI * t) / L);
    const g = G_peak * sinNorm * sinNorm;                  // sin² shape, kW/m²
    const pDc = arrayKwp * g * etaSystem;                  // DC kW available
    let q = 0;                                             // L/min
    let pUse = 0;                                          // kW actually drawn by pump
    if (pDc >= P_cutIn) {
      pUse = Math.min(pDc, pumpKw);                        // controller clips
      // Step 1 — flow available from current DC power alone (no head limit)
      let qBase = qRatedLpm * Math.pow(pUse / pumpKw, alpha);

      if (dynamicHead) {
        // Step 2 — iterate to find the operating point on the Q-H curve.
        //   H(Q) = H_static + H_friction × (Q/Q_rated)^1.85
        //   Q_op = qBase × √((h0 − H(Q)) / (h0 − h_rated))   if H_rated < H(Q) < h0
        //   Q_op = qBase                                      if H(Q) ≤ H_rated
        //   Q_op = 0                                          if H(Q) ≥ h0
        // Two passes are enough — converges to <1 % for realistic values.
        let qIter = qBase;
        for (let k = 0; k < 3; k++) {
          const frictionNow = hFrictionRatedM * Math.pow(qIter / qRatedLpm, 1.85);
          const hNow = hStaticM + frictionNow;
          let derate;
          if (hNow >= h0)        derate = 0;
          else if (hNow <= hRatedM) derate = 1;
          else                   derate = Math.sqrt(Math.max(0, (h0 - hNow) / (h0 - hRatedM)));
          qIter = qBase * derate;
          if (qIter <= 0) break;
        }
        q = qIter;
      } else {
        q = qBase;
      }

      if (q > 0) {
        pumpingMinutes += dt * 60;
        if (q > peakFlow) peakFlow = q;
      } else {
        // Pump tried to run but head exceeded shutoff at this instant —
        // count no flow and no energy (controller idles).
        pUse = 0;
      }
    }
    // Trapezoidal contribution. dt is in hours.
    const weight = (i === 0 || i === steps) ? 0.5 : 1.0;
    volume += weight * q * dt * 60;
    energy += weight * pUse * dt;
  }
  return {
    volumeLitres: volume,
    energyKwh: energy,
    pumpingHours: pumpingMinutes / 60,
    peakFlowLpm: peakFlow,
    runsAtAll: pumpingMinutes > 0,
    alpha,
    cutInKw: P_cutIn,
    gPeak: G_peak,
  };
}
